import React, { useEffect, useState } from 'react'
import {
  Table,
  Button,
  Modal,
  Input,
  Space,
  Select,
  DatePicker,
  Tag,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  SlackOutlined,
  FilePdfOutlined,
} from '@ant-design/icons'
import axiosInstance from '../../Utils/axiosInstance'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'

import {
  GetCustomerDataAPI,
  UpdateSalesDataAPI,
  CreateSalesDataAPI,
  GetSalesDataAPI,
  GetDeliveryDataOfCustomer,
  GetMultipleDeliveryDetailsOfCustomer,
  BaseIp,
  ViewSalesInvoicePdf,
} from '../../AllAPI/AdminAPI'

const SalesEntry = () => {
  const selectedYear = Number(sessionStorage.getItem('year'))
  const [sales, setSales] = useState([])
  const [customers, setCustomers] = useState([])
  const [searchText, setSearchText] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [actionLoadingId, setActionLoadingId] = useState(null)
  const [addBtnLoading, setAddBtnLoading] = useState(false)

  /* ================= Modal ================= */
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)

  /* ================= Inputs ================= */
  const [date, setDate] = useState(null)
  const [customerId, setCustomerId] = useState(null)
  const [remark, setRemark] = useState('')
  const [paymentStatus, setPaymentStatus] = useState('')
  const [loadingCharges, setLoadingCharges] = useState('')
  const [discountAmt, setDiscountAmt] = useState('')

  /* ================= Bills ================= */
  const [bills, setBills] = useState([])
  const [selectedBills, setSelectedBills] = useState([])
  const [billTableData, setBillTableData] = useState([])
  const [grossQty, setGrossQty] = useState(0)
  const [grossAmount, setGrossAmount] = useState(0)
  const [netGrossAmount, setNetGrossAmount] = useState(0)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [viewRecord, setViewRecord] = useState(null)

  useEffect(() => {
    fetchCustomers()
    fetchSalesData()
  }, [])

  useEffect(() => {
    const net =
      Number(grossAmount || 0) +
      Number(loadingCharges || 0) -
      Number(discountAmt || 0)

    setNetGrossAmount(net)
  }, [grossAmount, loadingCharges, discountAmt])

  /* ================= Fetch Sales ================= */
  const fetchSalesData = async () => {
    try {
      setLoading(true)
      const res = await axiosInstance.get(`${GetSalesDataAPI}/${selectedYear}`)

      const formatted = (Array.isArray(res.data) ? res.data : []).map(
        (item) => ({
          id: item.sales_id,
          cust_id: item.cust_id,
          discount_amount: item.discount_amount,
          date: dayjs(item.date).format('DD-MM-YYYY'),
          customer_name: item.customer_name || '-', // if backend sends it
          payment_status: item.payment_status === 1 ? 'Paid' : 'Pending',
          loading_charges: item.loading_charges,
          gross_quantity: item.gross_quantity,
          gross_total: item.gross_total,
          remark: item.remark,
          salesDetails: item.salesDetails || [],
        }),
      )

      setSales(formatted)
    } catch {
      toast.error('Failed to load sales data')
    } finally {
      setLoading(false)
    }
  }

  /* ================= Fetch Customers ================= */
  const fetchCustomers = async () => {
    const res = await axiosInstance.get(`${GetCustomerDataAPI}/${selectedYear}`)
    setCustomers(Array.isArray(res.data) ? res.data : [])
  }

  const resetOnCustomerChange = () => {
    setBills([])
    setSelectedBills([])
    setBillTableData([])
    setGrossQty(0)
    setGrossAmount(0)
    setNetGrossAmount(0)
    setLoadingCharges('')
    setDiscountAmt('')
    setPaymentStatus('')
    setRemark('')
  }

  /* ================= Fetch Bills By Customer ================= */
  const fetchBillsByCustomer = async (custId) => {
    try {
      const res = await axiosInstance.get(
        `${GetDeliveryDataOfCustomer}/${custId}`,
      )
      setBills(Array.isArray(res.data) ? res.data : [])
    } catch {
      toast.error('Failed to load bills')
    }
  }

  /* ================= Bill Select ================= */

  const handleBillSelect = (newSelected) => {
    // already selected invoiced bills
    const lockedBills = selectedBills.filter(isInvoicedBill)

    // user selected + locked ones
    const finalSelection = Array.from(new Set([...newSelected, ...lockedBills]))

    setSelectedBills(finalSelection)

    if (finalSelection.length === 0) {
      setBillTableData([])
      setGrossQty(0)
      setGrossAmount(0)
      setNetGrossAmount(0)
      return
    }

    fetchDeliveryDetails(finalSelection)
  }

  const fetchDeliveryDetails = async (deliveryIds) => {
    try {
      const res = await axiosInstance.post(
        GetMultipleDeliveryDetailsOfCustomer,
        {
          cust_id: customerId,
          delivery_ids: deliveryIds,
        },
      )

      const data = Array.isArray(res.data) ? res.data : []

      const tableData = data.map((d) => {
        const totalQty = d.items.reduce((sum, i) => sum + Number(i.quantity), 0)

        const totalAmt = d.items.reduce(
          (sum, i) => sum + Number(i.quantity) * Number(i.rate),
          0,
        )

        return {
          key: d.delivery_id,
          delivery_id: d.delivery_id,
          delivery_date: dayjs(d.delivery_date).format('DD-MM-YYYY'),
          total_qty: totalQty,
          total_amt: totalAmt,
        }
      })

      setBillTableData(tableData)

      const grossQty = tableData.reduce((sum, i) => sum + i.total_qty, 0)
      const grossAmount = tableData.reduce((sum, i) => sum + i.total_amt, 0)
      const netGrossAmount =
        Number(grossAmount || 0) +
        Number(loadingCharges || 0) -
        Number(discountAmt || 0)

      setGrossQty(grossQty)
      setGrossAmount(grossAmount)
      setNetGrossAmount(netGrossAmount)
    } catch (err) {
      toast.error('Failed to load delivery details')
    }
  }

  /* ================= Open Modal ================= */
  const openModal = () => {
    setEditingId(null)
    setDate(null)
    setCustomerId(null)
    setRemark('')
    setPaymentStatus(null)
    setLoadingCharges('')
    setBills([])
    setSelectedBills([])
    setBillTableData([])
    setGrossQty(0)
    setGrossAmount(0)
    setNetGrossAmount(0)
    setIsModalOpen(true)
  }

  /* ================= Save Data ================= */
  const handleSave = async () => {
    if (!date) return toast.error('Date required')
    if (!customerId) return toast.error('Customer required')
    if (!paymentStatus) return toast.error('Payment status required')
    if (billTableData.length === 0)
      return toast.error('Select at least one bill')

    // build items array from table
    const items = billTableData.map((b) => ({
      delivery_id: b.delivery_id,
      delivery_date: dayjs(b.delivery_date, 'DD-MM-YYYY').format('YYYY-MM-DD'),
      total_quantity: b.total_qty,
      total_amount: b.total_amt,
    }))

    const selectedCustomer = customers.find((c) => c.id === customerId)

    const customerName = selectedCustomer?.customer_name || ''
    const customerNumber = selectedCustomer?.contact || ''
    const customerCity = selectedCustomer?.city?.city_name || ''

    const payload = {
      date: date.format('YYYY-MM-DD'),
      cust_id: customerId,
      year: selectedYear,
      customer_name: customerName,
      customer_num: customerNumber,
      customer_city: customerCity,
      loading_charges: Number(loadingCharges) || 0,
      discount_amount: Number(discountAmt) || 0,
      gross_total: grossAmount,
      gross_quantity: grossQty,
      payment_status: paymentStatus === 'Paid' ? 1 : 0,
      remark,
      items,
    }

    try {
      setSubmitLoading(true)
      let res

      if (editingId) {
        //  UPDATE
        res = await axiosInstance.put(
          `${UpdateSalesDataAPI}/${editingId}`,
          payload,
        )

        if (res.data?.message === 'SALESUPDATED') {
          toast.success('Sales updated successfully')

          if (res.data.invoice) {
            const pdfPath = res.data.invoice.replace(/\\/g, '/')
            const pdfUrl = `${BaseIp}${pdfPath}`

            window.open(pdfUrl, '_blank')
          }
        } else {
          toast.warn('Update failed')
        }
      } else {
        //  CREATE
        res = await axiosInstance.post(CreateSalesDataAPI, payload)

        if (res.data?.message === 'SAVED') {
          toast.success('Sales added successfully')

          if (res.data.invoice) {
            const pdfPath = res.data.invoice.replace(/\\/g, '/')
            const pdfUrl = `${BaseIp}${pdfPath}`

            window.open(pdfUrl, '_blank')
          }
        } else {
          toast.warn('Save failed')
        }
      }

      fetchSalesData()
      setIsModalOpen(false)
    } catch (err) {
      toast.error('Failed to save sales')
    } finally {
      setSubmitLoading(false)
    }
  }

  /* ================= Edit Data ================= */
  const handleEdit = async (record) => {
    setEditingId(record.id)
    setIsModalOpen(true)

    // basic fields
    setDate(dayjs(record.date, 'DD-MM-YYYY'))
    setRemark(record.remark || '')
    setPaymentStatus(record.payment_status)
    setLoadingCharges(record.loading_charges || 0)
    setDiscountAmt(record.discount_amount || 0)

    // 1️⃣ set customer
    setCustomerId(record.cust_id)

    // 2️⃣ load bills for that customer FIRST
    await fetchBillsByCustomer(record.cust_id)

    // 3️⃣ extract delivery_ids from salesDetails
    const deliveryIds = record.salesDetails.map((d) => d.delivery_id)

    // 4️⃣ set selected bills (this sets dropdown value)
    setSelectedBills(deliveryIds)

    // 5️⃣ prepare table
    const tableData = record.salesDetails.map((d) => ({
      key: d.delivery_id,
      delivery_id: d.delivery_id,
      delivery_date: dayjs(d.delivery_date).format('DD-MM-YYYY'),
      total_qty: d.total_quantity,
      total_amt: d.total_amount,
    }))

    setBillTableData(tableData)

    // totals
    const qty = tableData.reduce((s, i) => s + i.total_qty, 0)
    const amt = tableData.reduce((s, i) => s + i.total_amt, 0)

    setGrossQty(qty)
    setGrossAmount(amt)

    const netGrossTotal =
      Number(amt || 0) +
      Number(record.loading_charges || 0) -
      Number(record.discount_amount || 0)

    setNetGrossAmount(netGrossTotal)
  }

  // =========Open Invoice PDF===========
  const handleOpenPdf = async (record) => {
    try {
      const res = await axiosInstance.get(
        `${ViewSalesInvoicePdf}/${record.id}/${record.cust_id}`,
      )

      if (res.data?.invoicePath) {
        const pdfPath = res.data.invoicePath.replace(/\\/g, '/')
        const pdfUrl = `${BaseIp}${pdfPath}`

        window.open(pdfUrl, '_blank')
      } else {
        toast.error('PDF not found')
      }
    } catch (err) {
      toast.error('Failed to load PDF')
    }
  }

  /* ================= Table ================= */
  const columns = [
    { title: 'Sale Id', dataIndex: 'id' },
    {
      title: 'Date',
      dataIndex: 'date',
      // render: (d) => dayjs(d).format('DD-MM-YYYY'),
    },
    { title: 'Customer Name', dataIndex: 'customer_name' },
    {
      title: 'Total Sell Qty',
      dataIndex: 'gross_quantity',
      render: (value) => (
        <div style={{ textAlign: 'right' }}>{Number(value)}</div>
      ),
    },
    {
      title: 'Total Amt (₹)',
      dataIndex: 'gross_total',
      render: (value) => (
        <div style={{ textAlign: 'right' }}> {Number(value)}</div>
      ),
    },
    {
      title: 'Loading Charges (₹)',
      dataIndex: 'loading_charges',
      render: (value) => (
        <div style={{ textAlign: 'right' }}> {Number(value)}</div>
      ),
    },
    {
      title: 'Discount Amt (₹)',
      dataIndex: 'discount_amount',
      render: (value) => (
        <div style={{ textAlign: 'right' }}> {Number(value)}</div>
      ),
    },
    {
      title: 'Net Total Amt (₹)',
      render: (_, record) => {
        const gross = Number(record.gross_total || 0)
        const loading = Number(record.loading_charges || 0)
        const discount = Number(record.discount_amount || 0)

        const netTotal = gross + loading - discount

        return (
          <div style={{ textAlign: 'right', fontWeight: 600 }}>{netTotal}</div>
        )
      },
    },
    { title: 'Payment Status', dataIndex: 'payment_status' },
    // {
    //   title: 'Actions',
    //   render: (_, record) => (
    //     <Space>
    //       <Button
    //         icon={<EyeOutlined />}
    //         size="small"
    //         title="View"
    //         onClick={() => {
    //           setViewRecord(record)
    //           setIsViewModalOpen(true)
    //         }}
    //       />
    //       <Button
    //         icon={<EditOutlined />}
    //         size="small"
    //         title="Edit"
    //         onClick={() => handleEdit(record)}
    //       />
    //       <Button
    //         icon={<FilePdfOutlined />}
    //         size="small"
    //         title="Pdf"
    //         type="primary"
    //         danger
    //         // disabled={record.payment_status === 'Pending'}
    //         onClick={() => handleOpenPdf(record)}
    //       />
    //     </Space>
    //   ),
    // },
    {
      title: 'Actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            size="small"
            loading={actionLoadingId === `view-${record.id}`}
            onClick={() => {
              setActionLoadingId(`view-${record.id}`)
              setViewRecord(record)
              setIsViewModalOpen(true)
              setActionLoadingId(null)
            }}
          />

          <Button
            icon={<EditOutlined />}
            size="small"
            loading={actionLoadingId === `edit-${record.id}`}
            onClick={() => {
              setActionLoadingId(`edit-${record.id}`)
              handleEdit(record)
              setActionLoadingId(null)
            }}
          />

          <Button
            icon={<FilePdfOutlined />}
            size="small"
            type="primary"
            danger
            loading={actionLoadingId === `pdf-${record.id}`}
            onClick={async () => {
              setActionLoadingId(`pdf-${record.id}`)
              await handleOpenPdf(record)
              setActionLoadingId(null)
            }}
          />
        </Space>
      ),
    },
  ]

  const billColumns = [
    { title: 'Delivery ID', dataIndex: 'delivery_id' },
    { title: 'Delivery Date', dataIndex: 'delivery_date' },
    { title: 'Total Qty', dataIndex: 'total_qty' },
    { title: 'Total Amount (₹)', dataIndex: 'total_amt' },
  ]

  const filteredSales = sales.filter((s) =>
    s.customer_name?.toLowerCase().includes(searchText.toLowerCase()),
  )

  const viewDetailsColumns = [
    { title: 'Delivery ID', dataIndex: 'delivery_id' },
    {
      title: 'Delivery Date',
      dataIndex: 'delivery_date',
      render: (d) => dayjs(d).format('DD-MM-YYYY'),
    },
    { title: 'Total Qty', dataIndex: 'total_quantity' },
    { title: 'Total Amount (₹)', dataIndex: 'total_amount' },
  ]

  const isInvoicedBill = (deliveryId) => {
    const bill = bills.find((b) => b.delivery_id === deliveryId)
    return bill?.status === 2
  }

  const visibleBills = bills.filter((b) => {
    // CREATE MODE → hide invoiced bills
    if (!editingId) {
      return b.status !== 2
    }

    // EDIT MODE → show invoiced ONLY if already selected
    if (b.status === 2) {
      return selectedBills.includes(b.delivery_id)
    }

    return true
  })

  return (
    <>
      <div className="mainContainer">
        <h3>
          <SlackOutlined style={{ color: 'orangered' }} /> Sales Entry
        </h3>

        <div className="search-bar text-end">
          <Button
            icon={<PlusOutlined />}
            className="addButton mx-2"
            loading={addBtnLoading}
            onClick={() => {
              setAddBtnLoading(true)
              openModal()
              setAddBtnLoading(false)
            }}
          >
            Add
          </Button>

          <Input
            placeholder="Search customer..."
            style={{ width: 250 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        <Table
          columns={columns}
          dataSource={filteredSales}
          rowKey="id"
          loading={loading}
          bordered
          style={{ overflowX: 'auto' }}
        />
      </div>

      {/* ================= MODAL ================= */}
      <Modal
        title="Add Sales Entry"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={handleSave}
        okText="Save"
        width={900}
        okButtonProps={{
          loading: submitLoading,
        }}
      >
        <div className="row">
          <div className="col-12 col-md-4 pt-2">
            <label>Date</label>
            <DatePicker
              value={date}
              onChange={setDate}
              style={{ width: '100%' }}
            />
          </div>
          <div className="col-12 col-md-8 pt-2">
            <label>Customer</label>
            <Select
              value={customerId}
              disabled={editingId}
              style={{ width: '100%' }}
              placeholder="Select customer"
              onChange={(val) => {
                setCustomerId(val)
                if (!editingId) {
                  resetOnCustomerChange()
                }
                fetchBillsByCustomer(val)
              }}
            >
              {customers.map((c) => (
                <Select.Option key={c.id} value={c.id}>
                  {c.customer_name}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div className="col-12 col-md-8 pt-2">
            <label>Bills</label>
            <Select
              mode="multiple"
              value={selectedBills}
              onChange={handleBillSelect}
              placeholder="Select bills"
              style={{ width: '100%' }}
              tagRender={(props) => {
                const invoiced = isInvoicedBill(props.value)
                return (
                  <Tag
                    color={invoiced ? 'green' : 'default'}
                    closable={!invoiced}
                    onClose={props.onClose}
                  >
                    {props.label}
                  </Tag>
                )
              }}
            >
              {visibleBills.map((b) => (
                <Select.Option
                  key={b.delivery_id}
                  value={b.delivery_id}
                  disabled={b.status === 2}
                >
                  <div
                    style={{
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      lineHeight: '1.4',
                    }}
                  >
                    <b>Delivery Id:</b> {b.delivery_id}
                    &nbsp; | <b>Date:</b>{' '}
                    {dayjs(b.delivery_date).format('DD-MM-YYYY')}
                    {b.status === 1 && (
                      <Tag color="blue" style={{ marginLeft: 6 }}>
                        Delivered
                      </Tag>
                    )}
                    {b.status === 2 && (
                      <Tag color="green" style={{ marginLeft: 6 }}>
                        Invoiced
                      </Tag>
                    )}
                  </div>
                </Select.Option>
              ))}
            </Select>
          </div>
          <div className="col-12 col-md-4 pt-2">
            <label>Payment Status</label>
            <Select
              value={paymentStatus}
              onChange={setPaymentStatus}
              style={{ width: '100%' }}
            >
              <Select.Option value="Pending">Pending</Select.Option>
              <Select.Option value="Paid">Paid</Select.Option>
            </Select>
          </div>
        </div>

        {billTableData.length > 0 && (
          <>
            <Table
              columns={billColumns}
              dataSource={billTableData}
              pagination={false}
              size="small"
              bordered
              className="mt-3 "
              style={{ overflowX: 'auto' }}
            />

            <div className="row text-center border m-0 rounded shadow">
              <div className="col-12 col-md-4"></div>
              <div className="col-12 col-md-4 py-1">
                <b>Gross Total Qty:</b> {grossQty}
              </div>
              <div className="col-12 col-md-4 py-1">
                <b>Gross Total Amount:</b> ₹ {grossAmount}
              </div>
            </div>
            <div className="row mt-2">
              <div className="col-12 col-md-4 pt-2">
                <label>Loading Charges</label>
                <Input
                  type="number"
                  value={loadingCharges}
                  step="1"
                  onKeyDown={(e) => {
                    if (e.key === '.' || e.key === ',') {
                      e.preventDefault()
                    }
                  }}
                  onChange={(e) => setLoadingCharges(e.target.value)}
                />
              </div>
              <div className="col-12 col-md-4 pt-2">
                <label>Discount Amount (₹)</label>
                <Input
                  type="number"
                  value={discountAmt}
                  step="1"
                  onKeyDown={(e) => {
                    if (e.key === '.' || e.key === ',') {
                      e.preventDefault()
                    }
                  }}
                  onChange={(e) => setDiscountAmt(e.target.value)}
                />
              </div>
              <div className="col-12 col-md-4 pt-2">
                <label>Net Gross Amount (₹)</label>
                <Input
                  type="number"
                  value={netGrossAmount}
                  readOnly
                  style={{ backgroundColor: 'yellow' }}
                />
              </div>
            </div>
          </>
        )}

        <div className="row">
          <div className="col-12 col-md-4">
            <label className="mt-2">Remark</label>
            <Input.TextArea
              rows={2}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
            />
          </div>
        </div>
      </Modal>

      {/* ================= VIEW MODAL ================= */}
      <Modal
        title="View Sales Details"
        open={isViewModalOpen}
        footer={null}
        width={900}
        onCancel={() => {
          setIsViewModalOpen(false)
          setViewRecord(null)
        }}
      >
        {viewRecord && (
          <>
            <div className="row mb-2">
              <div className="col-md-4">
                <b>Date:</b> {viewRecord.date}
              </div>
              <div className="col-md-4">
                <b>Customer Name:</b> {viewRecord.customer_name}
              </div>
              <div className="col-md-4">
                <b>Payment Status:</b> {viewRecord.payment_status}
              </div>
            </div>

            <Table
              columns={viewDetailsColumns}
              dataSource={viewRecord.salesDetails}
              rowKey="delivery_id"
              pagination={false}
              bordered
              size="small"
              className="shadow"
              style={{ overflowX: 'auto' }}
            />

            <div className="row my-2">
              <div className="col-md-4 pt-2">
                <b>Loading Charges (₹):</b> {viewRecord.loading_charges}
              </div>
              <div className="col-md-4 pt-2">
                <b>Discount Amt (₹):</b> {viewRecord.discount_amount}
              </div>
              <div className="col-md-4 pt-2">
                <b>Total Sell Qty:</b> {viewRecord.gross_quantity}
              </div>
              <div className="col-md-4 pt-2">
                <b>Total Amount (₹):</b> {viewRecord.gross_total}
              </div>
              <div className="col-md-4 pt-2">
                <span className="p-2" style={{ backgroundColor: 'yellow' }}>
                  <b>Net Total Amount (₹):</b>{' '}
                  {Number(viewRecord.gross_total || 0) +
                    Number(viewRecord.loading_charges || 0) -
                    Number(viewRecord.discount_amount || 0)}
                </span>
              </div>
            </div>
          </>
        )}
      </Modal>
    </>
  )
}

export default SalesEntry
